import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getSkyStars } from "@/api/sky";
import { MainButton } from "@/ui/Buttons/MainButton/MainButton";
import mainButtonStyles from "@/ui/Buttons/MainButton/styles.module.scss";
import starArtwork from "@/assets/images/firefliesNight/star_1.png";
import skyImage from "./assets/fireflies_sky.png";
import { DeferredImage } from "@/ui/DeferredImage/DeferredImage";
import { useNearViewport } from "@/hooks/useNearViewport";
import {
  createPreviewSession,
  readPreviewSession,
  resolvePreviewStars,
  writePreviewSession,
} from "./previewSession";
import type { PreviewSession, PreviewStar } from "./previewSession";
import cls from "./styles.module.scss";

type Phase = "ready" | "leaving" | "entering";
const FADE_OUT_MS = 300;
const FADE_IN_MS = 850;
const positions = [
  [62, 26, 20, 50],
  [80, 37, 51, 50],
  [55, 59, 80, 50],
  [71, 64, 32, 58],
  [89, 58, 70, 58],
];

// StrictMode may mount twice: share the pending read, not stale sky data.
let pendingRead: ReturnType<typeof getSkyStars> | null = null;
function readSky() {
  if (!pendingRead) {
    const request = getSkyStars();
    pendingRead = request;
    const clear = () => {
      if (pendingRead === request) pendingRead = null;
    };
    void request.then(clear, clear);
  }
  return pendingRead;
}

export function HomepageSky() {
  const navigate = useNavigate();
  const [previewRef, nearby] = useNearViewport<HTMLDivElement>();
  const [initialSession] = useState(readPreviewSession);
  const sessionRef = useRef<PreviewSession | null>(initialSession);
  const [stars, setStars] = useState<PreviewStar[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [phase, setPhase] = useState<Phase>("ready");
  const [next, setNext] = useState<{
    session: PreviewSession;
    stars: PreviewStar[];
  } | null>(null);
  const mounted = useRef(false);
  const busy = useRef(false);

  useEffect(() => {
    if (!nearby) return;
    mounted.current = true;
    let cancelled = false;
    readSky()
      .then((items) => {
        if (cancelled) return;
        const session = initialSession ?? createPreviewSession(items);
        sessionRef.current = session;
        writePreviewSession(session);
        setStars(resolvePreviewStars(session, items));
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      mounted.current = false;
    };
  }, [initialSession, nearby]);

  useEffect(() => {
    if (phase === "ready") return;
    const timer = window.setTimeout(
      () => {
        if (phase === "leaving" && next) {
          sessionRef.current = next.session;
          writePreviewSession(next.session);
          setStars(next.stars);
          setNext(null);
          setPhase("entering");
        } else {
          busy.current = false;
          setPhase("ready");
        }
      },
      phase === "leaving" ? FADE_OUT_MS : FADE_IN_MS
    );
    return () => window.clearTimeout(timer);
  }, [phase, next]);

  async function shuffleStars() {
    if (busy.current || loading) return;
    busy.current = true;
    setLoading(true);
    setError(false);
    try {
      const items = await readSky();
      if (!mounted.current) return;
      const session = createPreviewSession(items, sessionRef.current);
      setNext({ session, stars: resolvePreviewStars(session, items) });
      setPhase("leaving");
    } catch {
      if (mounted.current) {
        setError(true);
        busy.current = false;
      }
    } finally {
      if (mounted.current) setLoading(false);
    }
  }

  const status = error
    ? "Не удалось загрузить звёздочки. Попробуй ещё раз."
    : loading
    ? "Звёздочки загружаются…"
    : phase !== "ready"
    ? "В небе появляются другие истории…"
    : stars.length
    ? `Показано историй: ${stars.length}. Выбери звёздочку, чтобы открыть страницу питомца.`
    : "Публичных звёздочек пока нет. Можно открыть Небо светлячков.";

  return (
    <div
      ref={previewRef}
      className={cls.preview}
      data-home-sky-preview
      data-sky-phase={phase}
    >
      <DeferredImage
        src={skyImage}
        width={1207}
        height={538}
        alt=""
        className={cls.background}
        aria-hidden="true"
        loading="lazy"
        decoding="async"
      />
      <div className={cls.copy}>
        <h2 id="sky-discovery-title">Небо светлячков</h2>
        <p className={cls.lead}>Здесь каждая звёздочка — чья-то любовь</p>
        <div className={cls.actions}>
          <MainButton
            label="Небо светлячков"
            onClick={() => navigate("/sky")}
          />
          <button
            type="button"
            className={`${mainButtonStyles.btn} ${cls.shuffle}`}
            disabled={loading || phase !== "ready"}
            aria-describedby="sky-preview-status"
            onClick={shuffleStars}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M4 11a8 8 0 0 1 13.66-4.66L20 9M20 4v5h-5M20 13a8 8 0 0 1-13.66 4.66L4 15M4 20v-5h5" />
            </svg>
            Случайные светлячки
          </button>
        </div>
        <p
          id="sky-preview-status"
          role="status"
          className={
            error || (!loading && !stars.length)
              ? cls.status
              : cls.visuallyHidden
          }
        >
          {status}
        </p>
      </div>
      <div
        className={cls.field}
        role="group"
        aria-label="Публичные звёздочки из Неба светлячков"
        aria-busy={loading || phase !== "ready"}
        data-sky-stars
      >
        {stars.map(({ star, slot, dx, dy }) => (
          <Link
            key={`${sessionRef.current?.revision}:${star.id}`}
            to={`/pets/${encodeURIComponent(star.pet_id!)}`}
            className={cls.star}
            style={
              {
                "--star-x": `${positions[slot][0] + dx}%`,
                "--star-y": `${positions[slot][1] + dy}%`,
                "--star-mobile-x": `${positions[slot][2] + dx}%`,
                "--star-mobile-y": `${positions[slot][3] + dy * 0.5}%`,
                "--star-color": /^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(
                  star.color
                )
                  ? star.color
                  : "#fdc268",
                "--star-image": `url(${starArtwork})`,
                "--appearance-delay": `${slot * 50}ms`,
              } as React.CSSProperties
            }
            aria-label={`Открыть страничку: ${star.name}`}
            tabIndex={phase === "leaving" ? -1 : 0}
            data-sky-star-id={star.id}
            data-sky-slot={slot}
          >
            <span className={cls.starImage} aria-hidden="true" />
            <span className={cls.starName}>{star.name}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}
